import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { BoardGeometry, Cell, PlayerColor } from '@/shared/board/types'
import type { FeedEvent, GameView, Move } from '@/shared/game/types'
import { cellOf } from './placement'
import { play } from './sound'

/** Сколько держится каждая клетка пути. */
const STEP_MS = 110

/** То же, когда очередь отстала: шаг вдвое короче, путь читается по-прежнему. */
const HURRY_STEP_MS = 55

/**
 * С какой длины очереди показ идёт ускоренно.
 *
 * Темп задаёт сервер, и запас там небольшой: пауза перед ходом бота — 2 с,
 * а показ броска занимает около двух (см. docs/RULES.md). На телефоне этого
 * запаса нет вовсе — кадры реже, таймеры в свёрнутой вкладке замедляются
 * до секунды, — и очередь начинает копиться. Раньше она копилась до
 * MAX_BACKLOG и отбрасывала сцены; теперь сначала ускоряется показ, и до
 * отбрасывания дело почти не доходит.
 *
 * Две сцены — это ровно один ход соперника (бросок и ход). Пока очередь не
 * длиннее, спешить незачем.
 */
const HURRY_AT = 2

/**
 * Сколько сцен подряд имеет смысл досматривать.
 *
 * Вкладка могла спать, пока соперники играли, — и тогда в ленте ждёт десяток
 * ходов. Проигрывать их все значит смотреть чужую партию в записи вместо
 * своей: показываем последние и догоняем.
 *
 * Больше, чем ход соперника с добросами (бросок, ход, бросок, ход...):
 * отбрасывание — последнее средство, а не рабочий режим, и наступать оно
 * должно после ускорения, а не вместо него.
 */
const MAX_BACKLOG = 6

/**
 * Сколько ждать отчёта оверлея о показанном броске.
 *
 * Страховка, а не такт: обычно отчёт приходит секунды через три. Но
 * если оверлей исчезнет, не отчитавшись, очередь встанет навсегда, а вместе
 * с ней и вся партия на экране — включая приглашение бросить свои кубики.
 * Лучше показать следующую сцену, чем замереть.
 */
const ROLL_RESCUE_MS = 7000

/** Что показывается на доске по очереди: чужой бросок или чей-то ход. */
type Scene = { seq: number } & (
  | {
      kind: 'roll'
      color: PlayerColor
      dice: number[]
      who: string
      burned: boolean
      /** Доброс, а не обычный бросок: у него свой голос. */
      bonus: boolean
    }
  | {
      kind: 'move'
      color: PlayerColor
      token: number
      cells: Cell[]
      /** Кого этот ход срубит и где тот стоит, пока ход не показан. */
      victim: HeldToken | null
      /** Ход заканчивается в доме. Нужен только звуку: на доске это видно. */
      homed: boolean
      /** Ход выводит фишку из двора. Тоже только звуку. */
      exited: boolean
    }
)

/** Чужой бросок, который сейчас показывается над доской. */
export type ShownRoll = {
  seq: number
  dice: number[]
  who: string
  burned: boolean
  bonus: boolean
}

/** Бросок, уже дошедший до глаз игрока: он и лежит в центре доски. */
export type SeenRoll = {
  seq: number
  dice: number[]
  color: PlayerColor | null
  /** Доброс: звук у него свой, а на вид кубики те же. */
  bonus: boolean
}

/** Фишка на промежуточной клетке пути. */
export type WalkingToken = { color: PlayerColor; token: number; cell: Cell }

/**
 * Фишка, которую держат на месте, пока её сцена не сыграна.
 *
 * В состоянии партии фишка уже переехала — сервер отдаёт итог, а не то, что
 * видно игроку. Пока ход ждёт очереди, показывать надо, где фишка стояла:
 * иначе она оказывается в конце пути раньше, чем начинает по нему идти, и
 * анимация выглядит повтором уже случившегося. То же и со срубленной: она
 * стоит на своей клетке, пока её не сбили на глазах.
 */
export type HeldToken = { color: PlayerColor; token: number; cell: Cell }

/**
 * Очередь показа: броски и ходы идут по одному, в том порядке, в каком
 * случились.
 *
 * Раньше бросок и ход показывались независимо, и от этого всё время
 * наезжали друг на друга: фишка шла по доске, пока кубики ещё крутились, а
 * следующий бросок появлялся поверх недоигранного хода. Договориться о
 * пор ядке двум анимациям не о чем — каждая знает только про себя. Поэтому
 * очередь одна, и держит её лента партии: сцена доигрывается, курсор
 * сдвигается, начинается следующая.
 *
 * Своих бросков в очереди нет: их показывает кнопка, и ждут они не времени,
 * а нажатия.
 *
 * Темп задаёт сервер: и человек, и компьютер присылают действия с
 * человеческими промежутками (у бота это паузы в App\Jobs\PlayBotTurn).
 * Очередь только выстраивает их по порядку и не даёт наезжать друг на
 * друга — своих задержек она не добавляет.
 *
 * Пробовали наоборот, паузами здесь: клиент отставал от сервера всё
 * сильнее, очередь копилась, а переполнившись — отбрасывала сцены, и на
 * доске выходила чересполосица из ходов и бросков.
 */
export function usePlayback(
  view: GameView | undefined,
  board: BoardGeometry | undefined,
  mine: PlayerColor | null,
): {
  walking: WalkingToken | null
  roll: ShownRoll | null
  /** Оверлей отчитался: показан бросок с этим номером. */
  rollShown: (seq: number) => void
  held: HeldToken[]
  busy: boolean
  /**
   * Последний бросок, который игрок уже видел.
   *
   * Кубики лежат в центре доски постоянно, и показывать там надо именно его,
   * а не последний бросок из ленты: лента — состояние сервера, она уходит
   * вперёд, пока на доске доигрывается предыдущая сцена. Иначе в центре
   * лежало бы одно, а фишка шла по другому.
   */
  seenRoll: SeenRoll | null
  /** Очередь отстала — показывать надо быстрее. */
  hurry: boolean
  /** Чьё действие показывается сейчас. null — очередь пуста. */
  actor: PlayerColor | null
  /**
   * Номер последней сцены, которую очередь уже доиграла. null — ещё не
   * знаем, с чего начинать (первый рендер).
   *
   * Тем же номером метятся и реплики бота (ChatMessage.afterSeq): чат
   * ничем с этой очередью не связан сам по себе, и без сверки по курсору
   * реплика "шестёрка" появлялась бы раньше, чем кубики долетают до глаз.
   * Синхронизирует их useChat.reveal.
   */
  cursor: number | null
} {
  // Номер последней показанной сцены. null — ещё не знаем, с чего начинать.
  const [cursor, setCursor] = useState<number | null>(null)
  const [step, setStep] = useState(0)

  const scenes = useMemo(
    () => (cursor === null ? [] : scenesOf(view, board, mine, cursor)),
    [view, board, mine, cursor],
  )

  const next = scenes[0] ?? null
  const kind = next?.kind ?? null
  const seq = next?.seq ?? null
  const cells = next?.kind === 'move' ? next.cells.length : 0
  const hurry = scenes.length > HURRY_AT

  // Всё, что случилось до открытия страницы, показывать незачем: это чужая
  // история, а не ход, которого мы ждём.
  useEffect(() => {
    if (cursor !== null || view === undefined) return

    setCursor(view.feed[view.feed.length - 1]?.seq ?? 0)
  }, [cursor, view])

  // Догоняем, если отстали слишком сильно.
  useEffect(() => {
    if (scenes.length <= MAX_BACKLOG) return

    setCursor(scenes[trimAt(scenes)].seq - 1)
    setStep(0)
  }, [scenes])

  /*
   * Звуки событий подаются ЗДЕСЬ, а не на экране партии.
   *
   * Только очередь показа знает, когда событие дошло до глаз: в состоянии
   * партии фишка уже срублена и уже дома, а на доске в это время ещё идёт
   * предыдущий ход. Звук, поданный по состоянию, прозвучал бы за секунду
   * до самого удара.
   *
   * Ref, а не состояние: эффект ниже прогоняется повторно (StrictMode — по
   * два раза каждый), и повтор не должен превращаться в двойной удар.
   * Остальные звуки — на смену метки, useCueOnChange в sound.ts.
   */
  const cued = useRef<number | null>(null)
  /** Отложенный голос: заводится при отпускании фишки, гасится при уходе. */
  const arrival = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (arrival.current !== null) clearTimeout(arrival.current)
    },
    [],
  )
  const hit = next?.kind === 'move' && next.victim !== null
  const homed = next?.kind === 'move' && next.homed
  const exited = next?.kind === 'move' && next.exited

  // Ход идёт по клеткам; дошёл — сцена сыграна.
  useEffect(() => {
    if (kind !== 'move' || seq === null) return

    if (step >= cells) {
      /*
       * Один голос на сцену, и порядок здесь — это старшинство.
       *
       * Рубка и дом вместе не бывают: в домашней дорожке чужих фишек нет.
       * А вот выход из двора рубит запросто — на воротах старта может
       * стоять чужая фишка, — и тогда говорить надо про рубку: она
       * отбирает чужое, а выход случается по пять раз на игрока.
       */
      const voice = hit ? 'capture' : homed ? 'home' : exited ? 'exit' : null

      /*
       * Голос отстаёт на один шаг — и это не задержка, а совмещение.
       *
       * Здесь фишка ТОЛЬКО ОТПУСКАЕТСЯ на конечную клетку: место ей задаёт
       * разметка, а доводит её переход CSS длиной ровно в шаг показа
       * (.token в ui.css). Прозвучав сразу, удар приходился на начало
       * последнего прыжка, а фишка касалась клетки на сто с лишним
       * миллисекунд позже — на слух это уже заметно.
       *
       * Таймер, а не transitionend: событие пришлось бы тянуть через фишку
       * наружу, и оно не приходит вовсе, когда переход отключён
       * prefers-reduced-motion.
       *
       * Таймер живёт в ссылке и НЕ гасится уборкой этого эффекта: строки
       * ниже меняют состояние, эффект от этого прогоняется заново, и своя же
       * уборка убила бы отложенный голос, не дав ему прозвучать. Гасится он
       * только при уходе с экрана — отдельным эффектом ниже.
       *
       * Отметка ставится при ЗАВОДЕ таймера, а не в нём самом: иначе повтор
       * эффекта (StrictMode прогоняет их дважды) заводил бы второй.
       */
      if (voice !== null && cued.current !== seq) {
        cued.current = seq
        arrival.current = setTimeout(() => play(voice), hurry ? HURRY_STEP_MS : STEP_MS)
      }

      setCursor(seq)
      setStep(0)

      return
    }

    const timer = setTimeout(
      () => setStep((current) => current + 1),
      hurry ? HURRY_STEP_MS : STEP_MS,
    )

    return () => clearTimeout(timer)
  }, [kind, seq, step, cells, hurry, hit, homed, exited])

  // ...но не дольше страховочного срока: молчание оверлея не должно
  // останавливать партию на экране.
  useEffect(() => {
    if (kind !== 'roll' || seq === null) return

    const rescue = setTimeout(() => {
      setCursor(seq)
      setStep(0)
    }, ROLL_RESCUE_MS)

    return () => clearTimeout(rescue)
  }, [kind, seq])

  /*
   * Бросок показывается столько, сколько нужно оверлею: он и отчитывается.
   *
   * Отчёт обязан называть номер показанного броска, а не значить «сдвинь
   * очередь на одну сцену». Голова очереди меняется и без отчёта: сцены
   * могли отброситься за то время, пока кубики крутились. Безымянный отчёт
   * тогда сдвигал курсор мимо НОВОЙ головы — очередной бросок исчезал, не
   * показавшись, а его ходы оставались. На экране это и выглядело как
   * «кубиков у компьютера меньше, чем ходов».
   */
  const rollShown = useCallback(
    (shown: number) => {
      if (kind !== 'roll' || seq === null || shown !== seq) return

      setCursor(seq)
      setStep(0)
    },
    [kind, seq],
  )

  const walking =
    next?.kind === 'move' && step < next.cells.length
      ? { color: next.color, token: next.token, cell: next.cells[step] }
      : null

  /*
   * Свой бросок попадает сюда сразу: его показывает не очередь, а кнопка, и
   * ходить им игрок начинает немедленно. Чужой — когда очередь до него дошла.
   */
  const seenRoll = useMemo(
    () => (cursor === null ? null : seenRollOf(view, mine, cursor)),
    [view, mine, cursor],
  )

  const roll =
    next?.kind === 'roll'
      ? {
          seq: next.seq,
          dice: next.dice,
          who: next.who,
          burned: next.burned,
          bonus: next.bonus,
        }
      : null

  return {
    walking,
    roll,
    rollShown,
    held: heldOf(scenes, step),
    seenRoll,
    hurry,
    // Очередь не пуста: пока в ней что-то есть, своё приглашение бросить
    // кубики ждёт — даже если сервер уже передал ход нам.
    busy: scenes.length > 0,
    /*
     * Чей ход идёт на экране. Может отставать от партии: сервер передаёт
     * ход дальше сразу, а мы ещё доигрываем предыдущего. Подпись «ходит
     * такой-то» должна говорить про то, что видно, а не про то, что уже
     * записано в базе.
     */
    actor: next?.color ?? null,
    cursor,
  }
}

/**
 * Последний бросок, дошедший до глаз игрока.
 *
 * Свой — как только он случился. Чужой — только если очередь его проиграла,
 * то есть его номер не больше курсора.
 */
function seenRollOf(
  view: GameView | undefined,
  mine: PlayerColor | null,
  cursor: number,
): SeenRoll | null {
  if (view === undefined) return null

  for (let index = view.feed.length - 1; index >= 0; index -= 1) {
    const event = view.feed[index]

    if (event.type !== 'roll') continue

    const color = (event.payload.color as PlayerColor | undefined) ?? null

    if (event.seq > cursor && color !== mine) continue

    return {
      seq: event.seq,
      dice: (event.payload.dice as number[]) ?? [],
      color,
      bonus: event.payload.bonus === true,
    }
  }

  return null
}

/**
 * С какой сцены продолжать показ, когда очередь переполнилась.
 *
 * Не просто «оставить последние MAX_BACKLOG»: обрезка посреди хода оставляет
 * ходы без броска, которым они сделаны, — на доске фишки идут сами по себе,
 * а кубиков к ним нет. Поэтому от намеченного места идём вперёд до ближайшего
 * броска: пусть отбросится больше, зато то, что осталось, — целые ходы
 * соперников, кубики со своими ходами.
 *
 * Если броска впереди нет (в очереди одни ходы — например, свои: свой бросок
 * в очередь не идёт), оставляем как намечено: ходы без броска здесь и
 * задуманы.
 */
function trimAt(scenes: Scene[]): number {
  const from = scenes.length - MAX_BACKLOG

  for (let index = from; index < scenes.length; index += 1) {
    if (scenes[index].kind === 'roll') return index
  }

  return from
}

/**
 * Фишки, которых на доске ещё не коснулись показанные сцены.
 *
 * Первая сцена — та, что идёт прямо сейчас: её фишка стоит на клетке
 * анимации. Остальные ходы ещё даже не начинались, и их фишки держатся там,
 * откуда пойдут.
 */
function heldOf(scenes: Scene[], step: number): HeldToken[] {
  const held: HeldToken[] = []
  const seen = new Set<string>()

  const keep = (token: HeldToken): void => {
    const key = `${token.color}#${token.token}`

    // Первая сцена главнее: если фишка ходит дважды подряд (ход «на сумму»),
    // держать её надо там, откуда начнётся ближайший из ходов.
    if (seen.has(key)) return

    seen.add(key)
    held.push(token)
  }

  scenes.forEach((scene, index) => {
    if (scene.kind !== 'move') return

    const walking = index === 0

    if (walking && step >= scene.cells.length) return

    keep({
      color: scene.color,
      token: scene.token,
      cell: scene.cells[walking ? step : 0],
    })

    if (scene.victim !== null) {
      keep(scene.victim)
    }
  })

  return held
}

/**
 * Сцены, которые ещё не показаны.
 *
 * @param  mine  свой цвет: свои броски в очередь не идут
 */
function scenesOf(
  view: GameView | undefined,
  board: BoardGeometry | undefined,
  mine: PlayerColor | null,
  cursor: number,
): Scene[] {
  if (view === undefined || board === undefined) return []

  const scenes: Scene[] = []

  view.feed.forEach((event, index) => {
    if (event.seq <= cursor) return

    if (event.type === 'roll') {
      const color = event.payload.color as PlayerColor | undefined

      if (color === undefined || color === mine) return

      // За одним устройством кубики за людей бросает один и тот же человек:
      // показывать ему запись собственного броска незачем — он только что
      // видел его в оверлее. А вот бросок компьютера посмотреть надо.
      const human = view.players.find((player) => player.color === color)?.bot === null

      if (view.local === true && human) return

      // Сгоревший бросок виден по следующему событию: сервер пишет пропуск
      // сразу за броском, которым не сходить.
      const after = view.feed[index + 1]

      scenes.push({
        kind: 'roll',
        seq: event.seq,
        color,
        dice: (event.payload.dice as number[]) ?? [],
        who: view.players.find((player) => player.color === color)?.name ?? '—',
        burned: after?.type === 'skip' && after.payload.color === color,
        bonus: event.payload.bonus === true,
      })

      return
    }

    if (event.type !== 'move') return

    const walk = walkOf(event, board)

    if (walk === null) return

    scenes.push({ kind: 'move', seq: event.seq, ...walk })
  })

  return scenes
}

/**
 * Ход из ленты — клетками доски.
 *
 * По правилам противники видят сам ход, а не только его итог, — а в
 * состоянии партии фишка уже стоит на конечной клетке. Поэтому путь берётся
 * из ленты: он начинается там, откуда фишка пошла, и обрывается перед
 * конечной клеткой — её показывает уже само состояние.
 */
function walkOf(
  event: FeedEvent,
  board: BoardGeometry,
): {
  color: PlayerColor
  token: number
  cells: Cell[]
  victim: HeldToken | null
  homed: boolean
  exited: boolean
} | null {
  const move = event.payload as unknown as Move
  const path = move.path ?? []

  const cells = [move.from, ...path.slice(0, -1)]
    .map((position) => cellOf(board, move.color, position, move.token))
    .filter((cell): cell is Cell => cell !== null)

  if (cells.length === 0) return null

  return {
    color: move.color,
    token: move.token,
    cells,
    victim: victimOf(move, board),
    homed: move.to?.location === 'home',
    exited: move.kind === 'exit',
  }
}

/**
 * Срубленная фишка — и клетка, на которой она стоит до удара.
 *
 * Клетка та же, куда приходит рубящая: на одной клетке двух фишек не бывает,
 * поэтому рубят ровно того, кто стоит в конце пути.
 */
function victimOf(move: Move, board: BoardGeometry): HeldToken | null {
  if (move.captures === null) return null

  const cell = cellOf(board, move.captures.color, move.to, move.captures.token)

  return cell === null ? null : { color: move.captures.color, token: move.captures.token, cell }
}
